import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Send, FileText, Calendar } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import { Card, CardBody, Button, Input, TextArea, toast } from '../../components/common';
import { rfqApi, vendorPortalApi } from '../../services/api';
import './QuotationForm.css';

export default function QuotationForm() {
    const { rfqId, quotationId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const isEditing = !!quotationId;

    // Detect if accessing from vendor portal
    const isVendorPortal = location.pathname.includes('/vendor-portal');
    const [form, setForm] = useState({
        validUntil: '',
        notes: '',
        items: []
    });
    const [errors, setErrors] = useState({});

    // Fetch RFQ details
    const { data: rfq } = useQuery({
        queryKey: ['rfq', rfqId],
        queryFn: () => rfqApi.get(rfqId),
        select: (res) => res.data?.data
    });

    // Fetch existing quotation if editing
    const { data: quotation } = useQuery({
        queryKey: ['quotation', quotationId],
        queryFn: () => rfqApi.getQuotation(rfqId, quotationId),
        enabled: isEditing,
        select: (res) => res.data?.data
    });

    useEffect(() => {
        // Debug log to see what data is received
        console.log('QuotationForm RFQ data:', rfq);
        console.log('QuotationForm RFQ lines:', rfq?.lines);

        if (quotation) {
            setForm({
                validUntil: quotation.validUntil?.split('T')[0] || '',
                notes: quotation.notes || '',
                items: quotation.items || quotation.lines || []
            });
        } else if (rfq?.lines || rfq?.items) {
            // Initialize from RFQ lines (backend uses 'lines', fallback to 'items')
            const rfqLines = rfq.lines || rfq.items || [];
            console.log('Setting items from rfqLines:', rfqLines);
            setForm(prev => ({
                ...prev,
                items: rfqLines.map(item => ({
                    rfqItemId: item.id,
                    rfqLineId: item.id,
                    catalogItemId: item.catalogItemId || item.itemId,
                    itemName: item.itemName || item.catalogItem?.itemName || item.catalogItem?.name,
                    itemCode: item.itemCode || item.catalogItem?.itemCode,
                    quantity: item.quantity,
                    uom: item.uomCode || item.catalogItem?.unitOfMeasure?.code || 'PCS',
                    unitPrice: 0,
                    notes: ''
                }))
            }));
        }
    }, [rfq, quotation]);

    const saveMutation = useMutation({
        mutationFn: (data) => {
            if (isEditing) {
                return rfqApi.updateQuotation(rfqId, quotationId, data);
            }

            if (isVendorPortal) {
                // Use vendor portal API - calculates total from items
                const totalAmount = data.items.reduce((sum, item) =>
                    sum + (item.quantity * parseFloat(item.unitPrice || 0)), 0);

                return vendorPortalApi.submitQuotation(rfqId, {
                    items: data.items,
                    totalAmount,
                    validityDays: data.validUntil ?
                        Math.ceil((new Date(data.validUntil) - new Date()) / (1000 * 60 * 60 * 24)) : 30,
                    notes: data.notes,
                    termsAccepted: true
                });
            }

            // Use internal RFQ API
            return rfqApi.submitQuotation(rfqId, data);
        },
        onSuccess: () => {
            toast.success(`Quotation ${isEditing ? 'updated' : 'submitted'} successfully`);
            queryClient.invalidateQueries(['rfq', rfqId]);
            if (isVendorPortal) {
                navigate('/vendor-portal');
            } else {
                navigate(`/rfq/${rfqId}`);
            }
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to save');
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleItemChange = (index, field, value) => {
        setForm(prev => ({
            ...prev,
            items: prev.items.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        }));
    };

    const calculateTotal = () => {
        return form.items.reduce((sum, item) => sum + (item.quantity * (parseFloat(item.unitPrice) || 0)), 0);
    };

    const validate = () => {
        const newErrors = {};
        if (!form.validUntil) newErrors.validUntil = 'Valid until date is required';

        const hasInvalidPrice = form.items.some(item => !item.unitPrice || parseFloat(item.unitPrice) <= 0);
        if (hasInvalidPrice) newErrors.items = 'Please provide prices for all items';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        saveMutation.mutate({
            validUntil: form.validUntil,
            notes: form.notes,
            items: form.items.map(item => ({
                rfqItemId: item.rfqItemId,
                quantity: item.quantity,
                unitPrice: parseFloat(item.unitPrice),
                notes: item.notes
            }))
        });
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(value || 0);
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="quotation-form-page">
            <PageHeader
                title={isEditing ? 'Edit Quotation' : 'Submit Quotation'}
                subtitle={rfq?.rfqNumber}
                breadcrumbs={[
                    { label: 'Vendor Portal' },
                    { label: 'RFQ', path: '/vendor-portal/rfq' },
                    { label: rfq?.rfqNumber }
                ]}
            />

            <div className="quotation-form-content">
                <Card className="rfq-summary-card">
                    <CardBody>
                        <h3>RFQ Summary</h3>
                        <div className="rfq-summary">
                            <div className="summary-item">
                                <FileText size={16} />
                                <span>{rfq?.title}</span>
                            </div>
                            <div className="summary-item">
                                <Calendar size={16} />
                                <span>Deadline: {formatDate(rfq?.closingDate || rfq?.submissionDeadline)}</span>
                            </div>
                        </div>
                        {rfq?.description && (
                            <p className="rfq-description">{rfq.description}</p>
                        )}
                    </CardBody>
                </Card>

                <Card>
                    <CardBody>
                        <h3>Quotation Details</h3>
                        <div className="form-row">
                            <Input
                                type="date"
                                name="validUntil"
                                label="Valid Until"
                                value={form.validUntil}
                                onChange={handleChange}
                                error={errors.validUntil}
                                min={new Date().toISOString().split('T')[0]}
                                required
                            />
                        </div>
                    </CardBody>
                </Card>

                <Card>
                    <CardBody>
                        <h3>Price Your Items</h3>
                        {errors.items && <p className="items-error">{errors.items}</p>}

                        <div className="quotation-items">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th>Qty</th>
                                        <th>UOM</th>
                                        <th>Unit Price</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {form.items.map((item, index) => (
                                        <tr key={index}>
                                            <td>
                                                <div className="item-info">
                                                    <span className="item-code">{item.itemCode}</span>
                                                    <span className="item-name">{item.itemName}</span>
                                                </div>
                                            </td>
                                            <td>{item.quantity}</td>
                                            <td>{item.uom}</td>
                                            <td>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={item.unitPrice}
                                                    onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                                                    placeholder="Enter price"
                                                    className="price-input"
                                                />
                                            </td>
                                            <td className="total-cell">
                                                {formatCurrency(item.quantity * (parseFloat(item.unitPrice) || 0))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="4" className="grand-label">Grand Total</td>
                                        <td className="grand-total">{formatCurrency(calculateTotal())}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </CardBody>
                </Card>

                <Card>
                    <CardBody>
                        <TextArea
                            name="notes"
                            label="Additional Notes"
                            placeholder="Any additional information about your quotation..."
                            value={form.notes}
                            onChange={handleChange}
                            rows={3}
                        />
                    </CardBody>
                </Card>

                <div className="quotation-actions">
                    <Button variant="secondary" onClick={() => navigate(-1)}>
                        Cancel
                    </Button>
                    <Button icon={Send} onClick={handleSubmit} loading={saveMutation.isPending}>
                        Submit Quotation
                    </Button>
                </div>
            </div>
        </div>
    );
}
